/**
 * Árbol expandible de un reporte.
 *
 * REGLA: la jerarquía FORMAL del contador es la estructura; los grupos de Odoo
 * y las cuentas se injertan debajo. Este componente no sabe construir un árbol
 * desde `account.group`: recibe los nodos ya ordenados por el servidor y solo
 * decide qué se ve.
 *
 * El galón aparece ÚNICAMENTE donde hay hijos estructurales. Una cuenta con 24
 * movimientos no los tiene: sus apuntes se abren en un panel, y una flecha que
 * promete un despliegue inexistente es peor que no tenerla.
 */

import type { ReportTree, TreeNode } from "../api/types";
import { StatusBadge } from "./StatusBadge";
import { formatMoney, moneyClass } from "./format";
import { visibleNodes } from "./treeNodes";

type MoneyColumn = ReportTree["money_columns"][number];

/**
 * Juego de columnas de reserva.
 *
 * Solo se usa cuando el servidor no declara ninguna —un árbol de un reporte que
 * todavía no las publica—. El caso normal es que las declare: el Balance de
 * Comprobación lleva cuatro columnas de dinero en su Excel y llevaba dos en
 * pantalla, y esa diferencia venía de tener el juego escrito aquí.
 */
const COLUMNAS_POR_DEFECTO: MoneyColumn[] = [
  { field: "presented_period_amount", label: "Movimiento" },
  { field: "presented_closing_amount", label: "Saldo" },
];

/** Las columnas que se pintan sobre banda gris: la actividad del período. */
const MOVIMIENTO = new Set(["period_debit", "period_credit", "presented_period_amount"]);

interface Props {
  nodes: TreeNode[];
  expanded: Set<string>;
  onToggle: (nodeId: string) => void;
  onOpenEntries?: (node: TreeNode) => void;
  /** Columnas de dinero declaradas por el servidor para ESTE reporte. */
  moneyColumns?: MoneyColumn[];
  /** El total del árbol, ya calculado como suma de raíces. */
  totalRow?: ReportTree["total_row"];
}

export function TreeTable({
  nodes,
  expanded,
  onToggle,
  onOpenEntries,
  moneyColumns,
  totalRow,
}: Props) {
  if (nodes.length === 0) {
    return (
      <div className="table-wrap">
        <div className="state">
          <p>Sin renglones para el período seleccionado.</p>
        </div>
      </div>
    );
  }

  const visibles = visibleNodes(nodes, expanded);
  const columnas = moneyColumns?.length ? moneyColumns : COLUMNAS_POR_DEFECTO;

  return (
    <div className="table-wrap">
      <table className="tree">
        {/* Una sola geometría para la cabecera y para todas las filas. Con
            reparto automático, Concepto se quedaba 780 px y Trazabilidad 342
            para contener «1 > 11»: las columnas estaban alineadas —medido,
            delta 0— pero con esos huecos el ojo dejaba de creer que la cifra
            pertenecía a su rótulo. Concepto absorbe el resto, que es la
            columna que de verdad lo necesita. */}
        <colgroup>
          <col className="col-codigo" />
          <col />
          {columnas.map((columna) => (
            <col
              key={columna.field}
              className={
                MOVIMIENTO.has(columna.field) ? "col-cifra col-mov" : "col-cifra"
              }
            />
          ))}
          <col className="col-traza" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Código</th>
            <th scope="col">Concepto</th>
            {columnas.map((columna) => (
              <th
                key={columna.field}
                scope="col"
                className={MOVIMIENTO.has(columna.field) ? "num mov" : "num"}
              >
                {columna.label}
              </th>
            ))}
            <th scope="col">Trazabilidad</th>
          </tr>
        </thead>
        <tbody>
          {visibles.map((node) => (
            <tr key={node.node_id} className={rowClass(node)}>
              <td className="mono">{node.code}</td>
              <td>
                {/* La sangría va en el CONTENIDO, no en la celda: con
                    `padding-left` sobre el `<td>`, el borde izquierdo de
                    Concepto se movía con cada nivel y la columna dejaba de
                    tener una coordenada fija. */}
                <span
                  className="tree-sangria"
                  style={{ "--nivel": nivel(node.depth) } as React.CSSProperties}
                >
                {node.has_children ? (
                  <button
                    type="button"
                    className="tree-toggle"
                    aria-expanded={expanded.has(node.node_id)}
                    onClick={() => onToggle(node.node_id)}
                  >
                    <span aria-hidden="true" className="tree-chevron">
                      {expanded.has(node.node_id) ? "▾" : "›"}
                    </span>
                    {node.label}
                  </button>
                ) : (
                  <span className="tree-leaf">{node.label}</span>
                )}
                </span>
                <RuleNote node={node} />
              </td>
              {columnas.map((columna) => (
                <td
                  key={columna.field}
                  className={
                    MOVIMIENTO.has(columna.field)
                      ? `${moneyClass(node[columna.field])} mov`
                      : moneyClass(node[columna.field])
                  }
                >
                  {formatMoney(node[columna.field] as number)}
                </td>
              ))}
              {/* Dos hechos distintos, en dos líneas: los apuntes son una
                  ACCIÓN —abren el panel— y la ruta de Odoo es metadato de dónde
                  cuelga la cuenta en el plan. En la misma línea se leían como
                  una sola cadena, «24 movimientos → 4 > 41 > 4102». */}
              <td className="small tree-detalle">
                {node.node_type === "account" && node.entry_count > 0 && onOpenEntries && (
                  <button
                    type="button"
                    className="link"
                    onClick={() => onOpenEntries(node)}
                  >
                    {node.entry_count} movimientos →
                  </button>
                )}
                {node.odoo_path && (
                  <span className="muted mono tree-path" title="Ruta en el plan de cuentas de Odoo">
                    {node.odoo_path}
                  </span>
                )}
              </td>
            </tr>
          ))}
          {/* El hueco que deja pasar la última fila por debajo del total, que
              va pegado al fondo del contenedor que desplaza. Sin él, la última
              cuenta del árbol quedaba tapada por la fila de total. */}
          {totalRow && (
            <tr className="fila-hueco" aria-hidden="true">
              <td colSpan={columnas.length + 3} />
            </tr>
          )}
        </tbody>
        {/* El total va en `tfoot`: es la fila del documento, no una fila de
            datos, y así sobrevive a cualquier plegado del árbol. Su cifra es la
            SUMA DE RAÍCES que calculó el servidor —el mismo criterio que el
            `+D9+D54+D89+D106+D146` del Balance del contador—, no la suma de lo
            que el navegador tenga desplegado. */}
        {totalRow && (
          <tfoot>
            <tr className="fila-total">
              {/* Código y Concepto van juntos bajo el rótulo: la celda vacía
                  de Código se quedaba sin filete —`td:empty`— y la doble raya
                  del total empezaba a mitad de la tabla. */}
              <td colSpan={2} className="fila-total-rotulo">
                {totalRow.__label__}
              </td>
              {columnas.map((columna) => (
                <td
                  key={columna.field}
                  className={
                    MOVIMIENTO.has(columna.field)
                      ? `${moneyClass(totalRow[columna.field])} mov`
                      : moneyClass(totalRow[columna.field])
                  }
                >
                  {/* En blanco, no «—». `formatMoney(undefined)` devuelve una
                      raya, y en la fila de TOTAL esa raya dice «aquí había un
                      importe y no se pudo calcular» cuando lo cierto es que
                      esta columna no se totaliza a propósito —un saldo por
                      cuenta no se suma—. `DataTable` ya dejaba el hueco vacío,
                      así que el mismo reporte se contradecía según la vista. */}
                  {totalRow[columna.field] === undefined
                    ? ""
                    : formatMoney(totalRow[columna.field] as number)}
                </td>
              ))}
              <td />
            </tr>
            {/* Por qué unas columnas van en blanco, y si el cuadre salió. Es
                el MISMO pie que la vista «Tabla» de este reporte: se emitía
                solo en la tabla plana, así que un Debe distinto del Haber
                avisaba en una vista y en la otra no —el mismo reporte, con y
                sin alarma según el conmutador—. */}
            {(totalRow.__note__ || totalRow.__balanced__ === false) && (
              <tr className="fila-total-nota">
                <td colSpan={columnas.length + 3} className="small">
                  {totalRow.__balanced__ === false && (
                    <StatusBadge tone="serious">El Debe no iguala al Haber</StatusBadge>
                  )}{" "}
                  {totalRow.__note__}
                </td>
              </tr>
            )}
          </tfoot>
        )}
      </table>
    </div>
  );
}

/**
 * Una regla no se explica con un distintivo.
 *
 * Un renglón de ochenta millones que vale cero necesita las tres cifras: lo que
 * dice Odoo, lo que la regla mueve y lo que el estado presenta. Con solo el
 * cero, el importe habría desaparecido sin dejar rastro — que es exactamente lo
 * que esta plataforma existe para evitar.
 */
function RuleNote({ node }: { node: TreeNode }) {
  if (node.rule_ids.length === 0 && node.status === "ok") return null;
  return (
    <div className="tree-rule small muted">
      {node.status === "exception" && (
        <StatusBadge tone="serious">sin grupo en Odoo</StatusBadge>
      )}
      {node.is_excluded ? (
        <>
          <span>Odoo: {formatMoney(node.gross_closing_amount)}</span>
          <span>
            Excluido · {node.rule_ids.join(", ")}{" "}
            {formatMoney(node.rule_effect_closing)}
          </span>
        </>
      ) : (
        node.rule_ids.length > 0 && (
          <span>↳ Reclasificado para presentación · {node.rule_ids.join(", ")}</span>
        )
      )}
    </div>
  );
}

function rowClass(node: TreeNode): string | undefined {
  if (node.node_type === "report_line") {
    return node.kind === "source" ? undefined : `level-${Math.min(node.depth, 1)}`;
  }
  return node.node_type === "account_group" ? "tree-group" : "tree-account";
}

/** Nivel de sangría, acotado: a seis una tabla pierde todo el ancho útil.
 *
 * Devuelve el NIVEL y no los píxeles: los píxeles los pone la hoja de estilos,
 * porque la sangría es presentación. Son 18 px por nivel y no 14 — con la
 * sangría anterior, un grupo de Odoo colgando de un renglón formal se leía como
 * si estuviera al mismo nivel, y en el ESF eso hacía parecer que «4 COSTOS Y
 * GASTOS» era una partida del PATRIMONIO en vez de la composición de su
 * resultado del ejercicio, que es lo que es. */
function nivel(depth: number): number {
  return Math.min(Math.max(depth, 0), 6);
}
